import {
  Component,
  ChangeDetectionStrategy,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  ChangeDetectorRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { isBefore } from 'date-fns';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { CountdownTypes } from '@modules/countdown/enums/countdown-types.enum';
import { MediaStreamModel } from '@modules/media/models/media-stream.model';
import { VideoStatus } from '@modules/media/models/media-video.model';
import { StreamService } from '@modules/shared/services/stream.service';
import { Screen, WindowService } from '@modules/shared/services/window.service';
import { isContentOutdated } from 'src/helpers/dates';

import { CountdownComponent } from '../../countdown/countdown/countdown.component';
import { MediaComponent } from '../../media/media.component';
import { BufferingComponent } from '../../ui/buffering/buffering.component';
import { LiveIndicatorComponent } from '../../ui/live-indicator/live-indicator.component';

@Component({
  selector: 'mb-stream-card',
  templateUrl: './stream-card.component.html',
  styleUrls: ['./stream-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MediaComponent, LiveIndicatorComponent, CountdownComponent, BufferingComponent, RouterLink, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class StreamCardComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private windowService = inject(WindowService);
  private streamService = inject(StreamService);

  @Input() countdownType = CountdownTypes.ROUND;
  @Input() stream!: MediaStreamModel;

  public videoStatus = VideoStatus;
  public countdownTypes = CountdownTypes;

  public startDateReached = false;
  public countdownFinished = true;
  public isMobile = false;

  public get isArchived(): boolean {
    return isContentOutdated(this.stream?.startDate);
  }

  private destroy$ = new Subject<boolean>();
  private stopPoll$ = new Subject<boolean>();

  ngOnInit() {
    this.windowService.screen$.pipe(takeUntil(this.destroy$)).subscribe((screen) => {
      const isMobile = screen <= Screen.XS;
      if (isMobile !== this.isMobile) {
        this.isMobile = isMobile;
        this.cdr.detectChanges();
      }
    });

    // poll player data (automatically adapts poll interval based on status)
    this.streamService
      .pollPlayerStatusForStream(this.stream.id, this.stopPoll$)
      .pipe(takeUntil(this.destroy$))
      .subscribe((playerConfig) => {
        if (playerConfig) {
          this.stream.setPlayerConfig(playerConfig);
          this.cdr.detectChanges();
        }
      });

    this.startDateReached = this.stream.startDate ? isBefore(this.stream.startDate, new Date()) : false;
    this.setCountdownDateReached();
  }

  setCountdownDateReached() {
    this.countdownFinished = this.stream.countdownDate ? isBefore(this.stream.countdownDate, new Date()) : false;
    if (this.stream?.playerConfig?.id) {
      this.streamService
        .getPlayerConfig(this.stream.playerConfig.id)
        .pipe(take(1))
        .subscribe((playerConfig) => {
          if (playerConfig) {
            this.stream.setPlayerConfig(playerConfig);
            this.cdr.detectChanges();
          }
        });
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.stopPoll$.next(true);
    this.stopPoll$.complete();
  }
}
